import React, { useState } from 'react';
import { Modal, Badge } from 'react-bootstrap';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  Download,
  Printer,
  FileText,
  Search,
  BookOpen,
  Info,
  X,
  ExternalLink,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';

export default function JubileeDocumentViewerModal({ document, show, onHide }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fitMode, setFitMode] = useState('page'); // 'page' | 'width'
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!document) return null;

  const totalPages = document.pageCount || 12;
  const isPdf = document.mimeType?.includes('pdf') || document.fileName?.endsWith('.pdf');
  const isDocx = document.fileName?.endsWith('.docx') || document.fileName?.endsWith('.doc');

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageJump = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      setCurrentPage(val);
    }
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 25, 50));
  const handleFitWidth = () => {
    setFitMode(fitMode === 'width' ? 'page' : 'width');
    setZoomLevel(120);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="doc-viewer-modal"
      contentClassName="border-0 bg-transparent shadow-2xl"
    >
      <div className="doc-viewer-container">
        {/* Top App Bar (Google Drive Style Header) */}
        <div className="doc-viewer-header">
          <div className="d-flex align-items-center gap-3 overflow-hidden">
            <div
              className="rounded p-2 d-flex align-items-center justify-content-center"
              style={{ background: isPdf ? '#dc2626' : '#2563eb', color: '#fff', width: '36px', height: '36px' }}
            >
              <FileText size={20} />
            </div>
            <div className="overflow-hidden">
              <div className="fw-bold text-white text-truncate fs-6" style={{ maxWidth: '500px' }}>
                {document.title}
              </div>
              <div className="text-white-50 small d-flex align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                <span>{document.fileName}</span>
                <span>•</span>
                <span>{document.fileSize || 'PDF Document'}</span>
                <span>•</span>
                <span className="text-warning">{document.categoryName}</span>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light d-flex align-items-center gap-1 border-opacity-25"
              onClick={() => setShowSearch(!showSearch)}
              title="Search in document"
            >
              <Search size={16} />
              <span className="d-none d-md-inline small">Find</span>
            </button>

            <button
              className="btn btn-sm btn-outline-light d-flex align-items-center gap-1 border-opacity-25"
              onClick={handlePrint}
              title="Print document"
            >
              <Printer size={16} />
              <span className="d-none d-md-inline small">Print</span>
            </button>

            <a
              href={document.fileUrl}
              download={document.fileName}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-warning fw-semibold d-flex align-items-center gap-1 shadow-sm"
              title="Download original file"
            >
              <Download size={16} />
              <span className="d-none d-md-inline small">Download</span>
            </a>

            <button className="btn btn-link text-white-50 hover-text-white p-1 ms-2" onClick={onHide} title="Close Preview">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Toolbar (Page Navigation & Zoom Controls) */}
        <div className="doc-viewer-toolbar">
          {/* Page nav */}
          <div className="d-flex align-items-center gap-1">
            <button
              className="doc-tool-btn"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              title="Previous Page"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="d-flex align-items-center text-white small gap-1 px-1">
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={handlePageJump}
                className="form-control form-control-sm text-center bg-dark text-white border-secondary"
                style={{ width: '50px', padding: '0.2rem 0.4rem', fontSize: '0.8rem' }}
              />
              <span className="text-white-50">/ {totalPages}</span>
            </div>
            <button
              className="doc-tool-btn"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              title="Next Page"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="vr bg-secondary mx-2" style={{ height: '20px', opacity: 0.3 }} />

          {/* Zoom Controls */}
          <div className="d-flex align-items-center gap-1">
            <button className="doc-tool-btn" onClick={handleZoomOut} title="Zoom Out">
              <ZoomOut size={16} />
            </button>
            <span className="text-white-50 small px-2" style={{ fontFamily: 'monospace', minWidth: '48px', textAlign: 'center' }}>
              {zoomLevel}%
            </span>
            <button className="doc-tool-btn" onClick={handleZoomIn} title="Zoom In">
              <ZoomIn size={16} />
            </button>
            <button
              className={`doc-tool-btn ${fitMode === 'width' ? 'bg-primary border-primary text-white' : ''}`}
              onClick={handleFitWidth}
              title="Fit to Width"
            >
              <BookOpen size={16} />
              <span className="d-none d-lg-inline text-xs">Fit Width</span>
            </button>
          </div>

          <div className="vr bg-secondary mx-2 d-none d-md-block" style={{ height: '20px', opacity: 0.3 }} />

          {/* Fullscreen & Info Tab */}
          <div className="d-flex align-items-center gap-1">
            <button className="doc-tool-btn" onClick={toggleFullscreen} title="Fullscreen">
              {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
          </div>
        </div>

        {/* Search floating box */}
        {showSearch && (
          <div className="bg-dark p-2 border-bottom border-secondary border-opacity-25 d-flex align-items-center justify-content-center gap-2">
            <div className="input-group input-group-sm" style={{ maxWidth: '400px' }}>
              <span className="input-group-text bg-secondary border-secondary text-white">
                <Search size={14} />
              </span>
              <input
                type="text"
                className="form-control bg-dark text-white border-secondary"
                placeholder="Find in document..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button className="btn btn-outline-secondary text-white" onClick={() => setShowSearch(false)}>
                <X size={14} />
              </button>
            </div>
            {searchQuery && (
              <span className="text-success small">
                3 matches found in section {currentPage}
              </span>
            )}
          </div>
        )}

        {/* Main Body */}
        <div className="doc-viewer-body">
          {/* Document Canvas Area */}
          <div className="doc-canvas-area">
            {isPdf ? (
              /* Simulated Rich Document Reader for PDF */
              <div
                className="doc-simulated-page"
                style={{
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: 'top center',
                  width: fitMode === 'width' ? '95%' : '650px',
                  maxWidth: '900px',
                }}
              >
                {/* Simulated Institutional Header on Page 1 */}
                {currentPage === 1 && (
                  <div className="text-center pb-4 mb-4 border-bottom border-2 border-dark">
                    <div className="text-uppercase fw-bold" style={{ fontSize: '0.85rem', letterSpacing: '2px', color: '#198754' }}>
                      Jesuit Madurai Province Archives (JEMPARC)
                    </div>
                    <h3 className="fw-bold my-2" style={{ fontFamily: 'Georgia, serif', color: '#0f172a' }}>
                      {document.title}
                    </h3>
                    <div className="text-muted fst-italic small">
                      {document.eventName} • {document.year} • Official Archival Document
                    </div>
                  </div>
                )}

                {/* Simulated Document Content Body */}
                <div style={{ fontSize: '1rem', lineHeight: '1.8', color: '#1e293b' }}>
                  <p className="fw-bold" style={{ color: '#0f5132' }}>
                    Section {currentPage}: {currentPage === 1 ? 'Prologue and Historical Context' : `Chronicles and Decrees (Part ${currentPage})`}
                  </p>

                  <p>
                    {document.description}
                  </p>

                  <p>
                    In commemoration of the 75th Platinum Jubilee of the Jesuit Madurai Province (1951–2026),
                    this preserved record documents the historical continuity, administrative developments, and
                    missionary endeavors undertaken by the Society of Jesus across Tamil Nadu, South India, and overseas missions.
                  </p>

                  <blockquote className="p-3 my-3 bg-light border-start border-4 border-success fst-italic">
                    "From the humble beginnings at Shembaganur, Sacred Heart College, to expansive educational, social, and spiritual centres across the province, this archive preserves the living memory of selfless dedication."
                  </blockquote>

                  <p>
                    <strong>Archival Reference:</strong> JEMPARC/{document.year}/{document.id.toUpperCase()}
                    <br />
                    <strong>Author/Provenance:</strong> {document.author || document.person || 'Madurai Province Historical Commission'}
                    <br />
                    <strong>Category Classification:</strong> {document.categoryName}
                  </p>

                  {/* Decorative Footer */}
                  <div className="pt-4 mt-5 border-top d-flex justify-content-between text-muted small">
                    <span>MDU Platinum Jubilee Archive Repository</span>
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                </div>
              </div>
            ) : isDocx ? (
              /* Fallback Viewer for DOCX / Office Files */
              <div className="bg-white p-5 rounded-4 shadow text-center text-dark" style={{ maxWidth: '600px', margin: 'auto' }}>
                <div className="rounded-circle bg-primary bg-opacity-10 p-4 d-inline-flex mb-3 text-primary">
                  <FileText size={48} />
                </div>
                <h4 className="fw-bold mb-2">{document.title}</h4>
                <p className="text-muted mb-4 small">
                  This is a Microsoft Word Document (<code>{document.fileName}</code>). You can read its digitized summary metadata below or download the file to view with Microsoft Word.
                </p>

                <div className="p-3 bg-light rounded-3 text-start small mb-4">
                  <div><strong>File Format:</strong> Microsoft Word Document (.docx)</div>
                  <div><strong>Document Size:</strong> {document.fileSize}</div>
                  <div><strong>Archived Year:</strong> {document.year}</div>
                  <div><strong>Summary:</strong> {document.description}</div>
                </div>

                <div className="d-flex justify-content-center gap-3">
                  <a
                    href={document.fileUrl}
                    download={document.fileName}
                    className="btn btn-primary d-flex align-items-center gap-2"
                  >
                    <Download size={18} /> Download Document
                  </a>
                  <a
                    href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(document.fileUrl)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  >
                    <ExternalLink size={18} /> Open in Office Online
                  </a>
                </div>
              </div>
            ) : (
              /* Fallback for other formats */
              <div className="bg-white p-5 rounded-4 shadow text-center text-dark" style={{ maxWidth: '550px', margin: 'auto' }}>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3 d-inline-flex mb-3 text-warning">
                  <AlertCircle size={40} />
                </div>
                <h5 className="fw-bold mb-2">Preview unavailable for this file type</h5>
                <p className="text-muted small mb-4">
                  The document <code>{document.fileName}</code> is stored in its original archival format. You can download the authenticated file directly.
                </p>
                <a
                  href={document.fileUrl}
                  download={document.fileName}
                  className="btn btn-warning fw-semibold d-flex align-items-center justify-content-center gap-2"
                >
                  <Download size={18} /> Download Original File
                </a>
              </div>
            )}
          </div>

          {/* Metadata Sidebar (Google Drive Info Panel) */}
          <div className="doc-sidebar-meta">
            <h6 className="fw-bold text-white mb-3 d-flex align-items-center gap-2 border-bottom border-secondary border-opacity-25 pb-2">
              <Info size={16} className="text-warning" />
              Document Details
            </h6>

            <div className="mb-3">
              <span className="text-white-50 text-xs text-uppercase d-block">Collection / Category</span>
              <Badge bg="success" className="bg-opacity-25 text-success border border-success border-opacity-25 mt-1">
                {document.categoryName}
              </Badge>
            </div>

            <div className="mb-3">
              <span className="text-white-50 text-xs text-uppercase d-block">Event</span>
              <span className="small text-white fw-medium">{document.eventName || 'General Archive'}</span>
            </div>

            <div className="mb-3">
              <span className="text-white-50 text-xs text-uppercase d-block">Year of Origin</span>
              <span className="small text-white fw-medium">{document.year}</span>
            </div>

            {document.author && (
              <div className="mb-3">
                <span className="text-white-50 text-xs text-uppercase d-block">Author / Source</span>
                <span className="small text-white fw-medium">{document.author}</span>
              </div>
            )}

            {document.source && (
              <div className="mb-3">
                <span className="text-white-50 text-xs text-uppercase d-block">Physical Archive Shelf</span>
                <span className="small text-white fw-medium">{document.source}</span>
              </div>
            )}

            <div className="mb-3">
              <span className="text-white-50 text-xs text-uppercase d-block">Pages</span>
              <span className="small text-white fw-medium">{totalPages} pages</span>
            </div>

            <div className="mb-3">
              <span className="text-white-50 text-xs text-uppercase d-block">Integrity & Access</span>
              <span className="badge bg-dark border border-secondary text-info d-inline-flex align-items-center gap-1 mt-1">
                <ShieldCheck size={13} /> Verified Digital Archival Scan
              </span>
            </div>

            {document.tags && document.tags.length > 0 && (
              <div className="mt-3">
                <span className="text-white-50 text-xs text-uppercase d-block mb-1">Tags</span>
                <div className="d-flex flex-wrap gap-1">
                  {document.tags.map((t, idx) => (
                    <span key={idx} className="badge bg-secondary bg-opacity-25 text-white-50 border border-secondary border-opacity-50 text-xs">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
